import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { showcaseApi } from '../api/showcaseApi';
import type { JpaDemoItemResponse, ApiError } from '../types/showcase';

const SPIN_CSS = `@keyframes spin { to { transform: rotate(360deg); } }`;
const JPA_LIST_KEY = ['showcase', 'jpa', 'items'];

export function JpaPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [formLabel, setFormLabel] = useState('');
  const [formNote, setFormNote] = useState('');
  const [editingItem, setEditingItem] = useState<JpaDemoItemResponse | null>(null);
  const [flowStep, setFlowStep] = useState<'list' | 'create' | 'edit'>('list');
  const [lastAction, setLastAction] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: JPA_LIST_KEY,
    queryFn: () => showcaseApi.jpaList(0, 20),
  });

  const createMutation = useMutation<JpaDemoItemResponse, ApiError, { label: string; note?: string }>({
    mutationFn: (req) => showcaseApi.jpaCreate(req),
    onSuccess: (item) => {
      void queryClient.invalidateQueries({ queryKey: JPA_LIST_KEY });
      setFlowStep('list');
      setFormLabel('');
      setFormNote('');
      setLastAction(t('showcase.jpa.action.created', { label: item.label }));
    },
  });

  const updateMutation = useMutation<JpaDemoItemResponse, ApiError, { id: string; label: string; note?: string }>({
    mutationFn: ({ id, ...req }) => showcaseApi.jpaUpdate(id, req),
    onSuccess: (item) => {
      void queryClient.invalidateQueries({ queryKey: JPA_LIST_KEY });
      setFlowStep('list');
      setEditingItem(null);
      setLastAction(t('showcase.jpa.action.updated', { label: item.label }));
    },
  });

  const deleteMutation = useMutation<void, ApiError, string>({
    mutationFn: (id) => showcaseApi.jpaDelete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: JPA_LIST_KEY });
      setLastAction(t('showcase.jpa.action.deleted'));
    },
  });

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    createMutation.mutate({ label: formLabel, note: formNote || undefined });
  }

  function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    if (!editingItem) return;
    updateMutation.mutate({ id: editingItem.id, label: formLabel, note: formNote || undefined });
  }

  function startEdit(item: JpaDemoItemResponse) {
    setEditingItem(item);
    setFormLabel(item.label);
    setFormNote(item.note ?? '');
    setFlowStep('edit');
  }

  function cancelForm() {
    setFlowStep('list');
    setEditingItem(null);
    setFormLabel('');
    setFormNote('');
    createMutation.reset();
    updateMutation.reset();
  }

  return (
    <section aria-labelledby="jpa-panel-title">
      <style>{SPIN_CSS}</style>
      <h2
        id="jpa-panel-title"
        style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-2)', marginTop: 0 }}
      >
        {t('showcase.jpa.title')}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-4)' }}>
        {t('showcase.jpa.subtitle')}
      </p>

      {/* Flow breadcrumb */}
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', marginBottom: 'var(--spacing-4)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        <span style={{ color: flowStep === 'list' ? 'var(--color-brand-primary)' : 'var(--color-text-muted)', fontWeight: flowStep === 'list' ? 700 : 400 }}>
          {t('showcase.jpa.step.list')}
        </span>
        <span aria-hidden="true"> → </span>
        <span style={{ color: flowStep === 'create' ? 'var(--color-brand-primary)' : 'var(--color-text-muted)', fontWeight: flowStep === 'create' ? 700 : 400 }}>
          {t('showcase.jpa.step.create')}
        </span>
        <span aria-hidden="true"> → </span>
        <span style={{ color: flowStep === 'edit' ? 'var(--color-brand-primary)' : 'var(--color-text-muted)', fontWeight: flowStep === 'edit' ? 700 : 400 }}>
          {t('showcase.jpa.step.edit')}
        </span>
        <span aria-hidden="true"> → </span>
        <span>{t('showcase.jpa.step.delete')}</span>
      </div>

      {/* Last action feedback */}
      {lastAction && (
        <div
          role="status"
          style={{
            padding: 'var(--spacing-2) var(--spacing-3)',
            background: 'var(--color-sidebar-active-bg)',
            border: '1px solid var(--color-brand-primary)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-brand-primary)',
            marginBottom: 'var(--spacing-3)',
          }}
        >
          {lastAction}
        </div>
      )}

      {/* List view */}
      {flowStep === 'list' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-3)' }}>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', fontWeight: 600 }}>
              {t('showcase.jpa.list.title')}
              {data && (
                <span style={{ marginLeft: 'var(--spacing-2)', color: 'var(--color-text-muted)', fontWeight: 400 }}>
                  ({data.totalElements}{t('showcase.jpa.list.count')})
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={() => { setFlowStep('create'); setLastAction(null); }}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('showcase.jpa.list.createBtn')}
            </button>
          </div>

          {isLoading && (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-8)', color: 'var(--color-text-muted)' }}>
              <SpinIcon /> {t('showcase.jpa.list.loading')}
            </div>
          )}

          {isError && (
            <div role="alert" style={{ color: 'var(--color-danger)', padding: 'var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}>
              {t('showcase.jpa.list.loadError')}
            </div>
          )}

          {!isLoading && !isError && data && data.content.length === 0 && (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-8)', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
              {t('showcase.jpa.list.empty')}
            </div>
          )}

          {!isLoading && !isError && data && data.content.length > 0 && (
            <div style={{ border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
              <table
                aria-label={t('showcase.jpa.list.tableLabel')}
                style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {[t('showcase.jpa.col.id'), t('showcase.jpa.col.label'), t('showcase.jpa.col.note'), t('showcase.jpa.col.version'), t('showcase.jpa.col.createdAt'), t('showcase.jpa.col.actions')].map((h) => (
                      <th
                        key={h}
                        scope="col"
                        style={{ padding: 'var(--spacing-2) var(--spacing-3)', textAlign: 'left', fontWeight: 700, color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--color-surface-border)' }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.content.map((item, idx) => (
                    <tr key={item.id} style={{ borderBottom: idx < data.content.length - 1 ? '1px solid var(--color-surface-border)' : 'none' }}>
                      <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}><code style={{ fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-mono)' }}>{item.id}</code></td>
                      <td style={{ padding: 'var(--spacing-2) var(--spacing-3)', color: 'var(--color-text-primary)', fontWeight: 600 }}>{item.label}</td>
                      <td style={{ padding: 'var(--spacing-2) var(--spacing-3)', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>{item.note ?? '-'}</td>
                      <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}><code style={{ fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-mono)', color: 'var(--color-text-muted)' }}>v{item.version}</code></td>
                      <td style={{ padding: 'var(--spacing-2) var(--spacing-3)', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>
                        {item.createdAt ? new Date(item.createdAt).toLocaleString('ko-KR') : '-'}
                      </td>
                      <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                          <button
                            type="button"
                            onClick={() => { startEdit(item); setLastAction(null); }}
                            style={{ padding: 'var(--spacing-1) var(--spacing-2)', background: 'transparent', border: '1px solid var(--color-brand-primary)', borderRadius: 'var(--radius-sm)', color: 'var(--color-brand-primary)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-sans)' }}
                          >
                            {t('showcase.jpa.list.editBtn')}
                          </button>
                          <button
                            type="button"
                            disabled={deleteMutation.isPending}
                            onClick={() => deleteMutation.mutate(item.id)}
                            style={{ padding: 'var(--spacing-1) var(--spacing-2)', background: 'transparent', border: '1px solid var(--color-danger)', borderRadius: 'var(--radius-sm)', color: 'var(--color-danger)', cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer', fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-sans)' }}
                          >
                            {t('showcase.jpa.list.deleteBtn')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create / Edit form */}
      {(flowStep === 'create' || flowStep === 'edit') && (
        <form onSubmit={flowStep === 'create' ? handleCreate : handleUpdate}>
          <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--color-text-primary)', marginTop: 0, marginBottom: 'var(--spacing-4)' }}>
            {flowStep === 'create' ? t('showcase.jpa.form.createTitle') : t('showcase.jpa.form.editTitle')}
          </h3>

          <div style={{ display: 'grid', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
            <div>
              <label htmlFor="jpa-label" style={labelStyle}>{t('showcase.jpa.form.label')}</label>
              <input
                id="jpa-label"
                type="text"
                value={formLabel}
                onChange={(e) => setFormLabel(e.target.value)}
                maxLength={100}
                required
                disabled={createMutation.isPending || updateMutation.isPending}
                placeholder={t('showcase.jpa.form.labelPlaceholder')}
                style={inputStyle}
              />
            </div>
            <div>
              <label htmlFor="jpa-note" style={labelStyle}>{t('showcase.jpa.form.note')}</label>
              <textarea
                id="jpa-note"
                value={formNote}
                onChange={(e) => setFormNote(e.target.value)}
                maxLength={500}
                rows={3}
                disabled={createMutation.isPending || updateMutation.isPending}
                placeholder={t('showcase.jpa.form.notePlaceholder')}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>
          </div>

          {/* Mutation error */}
          {(createMutation.isError || updateMutation.isError) && (
            <div role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-3)' }}>
              {(createMutation.error ?? updateMutation.error)?.message}
            </div>
          )}

          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending || !formLabel.trim()}
              aria-busy={createMutation.isPending || updateMutation.isPending}
              style={primaryBtnStyle(createMutation.isPending || updateMutation.isPending || !formLabel.trim())}
            >
              {(createMutation.isPending || updateMutation.isPending) && <SpinIcon />}
              {flowStep === 'create' ? t('showcase.jpa.form.saveBtn') : t('showcase.jpa.form.updateBtn')}
            </button>
            <button type="button" onClick={cancelForm} style={secondaryBtnStyle}>
              {t('showcase.common.reset')}
            </button>
          </div>
        </form>
      )}

      {/* Learning */}
      <div
        style={{
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
          marginTop: 'var(--spacing-6)',
        }}
      >
        <span aria-hidden="true" style={{ marginRight: 'var(--spacing-2)' }}>📚</span>
        <strong>{t('showcase.panel.learning')}</strong> {t('showcase.jpa.learning')}
      </div>
    </section>
  );
}

// ─── Style helpers ────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontFamily: 'var(--font-family-sans)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-text-primary)',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  marginBottom: 'var(--spacing-1)',
};

function primaryBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: 'var(--spacing-3) var(--spacing-6)',
    background: disabled ? 'var(--color-text-muted)' : 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'var(--font-family-sans)',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
  };
}

const secondaryBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-3) var(--spacing-4)',
  background: 'transparent',
  color: 'var(--color-text-secondary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

function SpinIcon() {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        border: '2px solid rgba(255,255,255,0.3)',
        borderTopColor: 'var(--color-text-inverse)',
        borderRadius: '50%',
        display: 'inline-block',
        animation: 'spin 0.8s linear infinite',
      }}
    />
  );
}
